import {
	Form,
  Link,
  Links,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useLoaderData,
  useLocation,
} from "@remix-run/react";
import {
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {Analytics} from "@vercel/analytics/remix";
import { SpeedInsights } from "@vercel/speed-insights/remix"

import { json, redirect, type ActionFunctionArgs, type LinksFunction, type LoaderFunctionArgs } from "@remix-run/node";
import stylesheet from "~/globals.css?url";
import React, { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import '@stripe/stripe-js';

import { Layout } from "@/components/layout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { createSupabaseClient } from "@/lib/supabase.server";
import { getSession, resetProgressSession } from "./sessions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown"

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";
import Spinner from "@/components/spinner";
import { jwtDecode } from "jwt-decode";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
];

export const meta: MetaFunction = () => {
  return [
    { title: "Morby" },
    {
      property: "og:title",
      content: "Morby",
    },
    {
      name: "description",
      content: "Morby Wills",
    },
  ];
};

export async function loader({request}: LoaderFunctionArgs) {

  const {supabaseClient, headers} = createSupabaseClient(request, true);
  const user = (await supabaseClient.auth.getUser()).data.user;

  const userId = user?.id;

  // name of user for nav bar
  const name = user?.user_metadata.name ?? ''

  // console.log(name);
  const email = user?.email
 
  if(user){

    const date = new Date();
    const {error} = await supabaseClient.from('users')
      .update({
        last_active: date.toISOString(),
        inactivity_email_count: 0
      })
      .eq('id', userId)

    if(error){
      console.error('The error is: ', {error});
    }

    const session = (await supabaseClient.auth.getSession()).data.session;

    // if user is currently an admin, return this flag
    let adminMode = false;
    
    if(session){
        const jwt = jwtDecode(session.access_token)
            const userRole = jwt.user_role;
            // console.log(jwt);
            // console.log('the role of the user (dashboard) is: ', userRole)
            if(userRole === 'admin'){
                adminMode = true;
            }
    }
    

    return json({
      ENV: {
        SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
        SUPABASE_URL: process.env.SUPABASE_URL,
      },
      adminMode,
      name,
      email,
    }, {headers});

  }

  return json({
    ENV: {
      SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
      SUPABASE_URL: process.env.SUPABASE_URL,
    },
    adminMode: false,
    name,
    email: "",
  }, {headers});
}

export function ErrorBoundary() {
  const error = useRouteError() as Error;
  const location = useLocation();
  const fetcher = useFetcher();

  // Error details (you can also include more like stack trace, URL, etc.)
  const errorDetails = {
    status: isRouteErrorResponse(error) ? error.status : 500,
    message: error instanceof Error ? error.message : "Unknown Error",
    stack: error instanceof Error ? error.stack : null,
    url: isRouteErrorResponse(error) ? '/' : location.pathname,
  };


  captureRemixErrorBoundaryError(error);
  let errorMessage = "An unknown error occurred.";

  // Check if the error is a response error (like a 4xx or 5xx error)
  if (isRouteErrorResponse(error)) {
    errorMessage = `${error.status}: ${error.statusText}`;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }


  
  return (
	<Layout>
		<div className="flex w-full justify-center items-center ">
			<div className="w-full sm:w-1/2 flex flex-col gap-4 text-center">
				<h1 className="font-semibold text-4xl">Oops!</h1>
				<p className="text-2xl">{errorMessage}</p>
				<p className="sm:text-lg text-sm">Please send an email to <a className="text-[#a38ad8]" href="mailto:help@morby.xyz">help@morby.xyz </a> 
          to receive support. Let us know what you were doing when the error occurred.
				</p>

          {/* <ErrorReportForm {...errorDetails}/> */}

          <Form method="POST" action="/error-report" className="flex flex-col gap-2">

              <Textarea name="report" placeholder="Enter your report here" className="mb-3"/>
              
              <div className="flex justify-between gap-3 w-full">
                    <Button variant="outline" type="button" className="w-full" asChild>
                            <Link to={errorDetails.url} className="flex gap-2">
                                <img src="/arrow-left-white.svg"/>
                                Back
                            </Link>
                      </Button> 

                      <Button variant="default" type="submit" disabled={fetcher.state != 'idle'} className="w-full bg-[#7F56D9] text-white hover:bg-[#6941C6]">
                        {fetcher.state != 'idle' ? <Spinner/> : 'Submit Report'}
                    </Button>
              </div>
              <input type="hidden" name="url" value={errorDetails.url}/>
          </Form>

			</div>
		</div>
	</Layout>
		)

}

function Root(){

  const location = useLocation();
  const data = useLoaderData<typeof loader>();

  // Navigation dropdown
  const navigation = () => {
    

    const shouldHide = location.pathname.includes('/reset-password');

    if (shouldHide) return null;

    return (
        <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center"> 
                <Avatar className="mr-3 sm:block hidden">
                  <AvatarFallback className="text-[#525353] border border-[#525353]">
                    {data.adminMode ? 'A' : getInitials(data.name)}
                  </AvatarFallback>
                </Avatar>
                 <p className="line-clamp-1 sm:block hidden">{data.adminMode ? 'Admin' : data.name}</p>
                 <img src="/menu.svg" className="sm:hidden block"/>
              </DropdownMenuTrigger>
              <DropdownMenuContent avoidCollisions className="flex flex-col max-w-[300px] mr-1 bg-[#1A1A1A] border border-[#282829] [&_a]:border-[#282829] [&_a]:text-start text-sm [&_a]:text-[#CECFD2]">

                <DropdownMenuLabel className="flex p-4">
                  <Avatar className="mr-3">
                    <AvatarFallback className="text-[#525353] border border-[#282829]">
                      {data.adminMode ? 'A' : getInitials(data.name)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex flex-col gap-0 min-w-0">
                    <p className="text-sm font-semibold truncate ">{data.adminMode ? 'Admin' : data.name}</p>
                    <p className="text-sm text-[#525353] truncate">{data.email}</p>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator className="h-[1px]"/>

                {/* Only show these nav settings if user isn't an admin */}
                {data.adminMode === false &&
                  <div>
                    <DropdownMenuItem asChild className="h-12">
                      <Link className="flex gap-2 items-center hover:cursor-pointer" to="/"> 
                        <img src="/home.svg" className="w-4 filter brightness-50 invert-[33%]"/> 
                        Home
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="h-12">
                      <Link className="flex gap-2 items-center hover:cursor-pointer" to="/"> 
                        <img src="/edit.svg" className="w-4 filter brightness-50 invert-[33%]"/> 
                        My Will
                      </Link>
                    </DropdownMenuItem>


                    <DropdownMenuItem asChild className="h-12">
                      <Link className="flex gap-2 items-center hover:cursor-pointer" to="/settings">
                        <img src="/settings.svg" className="w-4 filter brightness-50 invert-[33%]"/> 
                        Settings
                      </Link>
                    </DropdownMenuItem>
                  </div>
                }
                 

                <DropdownMenuItem asChild className="h-12">
                  <form method="POST" action="/signout" className="p-0 m-0">
                    <Button type="submit" variant={null} className="text-[#CECFD2] w-full text-start justify-start flex gap-2 font-medium p-0 m-0 h-5">
                      <img src="/logout.svg" className=""/>
                      Logout
                    </Button>
                  </form>
                </DropdownMenuItem>

               
              </DropdownMenuContent>
      </DropdownMenu>
    )

  }



  return (
    <html lang="en" className="dark">
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com"/>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin=""/>
        <link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet"/>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen flex flex-col bg-[#1a1a1a]">
        <Analytics/>
        <SpeedInsights/>
        <noscript>
          <img height="1" width="1" className="hidden"
            src="https://www.facebook.com/tr?id=540690601840614&ev=PageView&noscript=1"
          />
        </noscript>
        {/* end pixel tracking code */}
        {/* <!-- Google Tag Manager (noscript) --> */}
        <noscript>
          <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-M865KT96"
          height="0" width="0" className="hidden"></iframe>
        </noscript>
        {/* <!-- End Google Tag Manager (noscript) --> */}

        {/* Header Section */}

        <div className="flex justify-between w-full 2xl:w-[1440px] mx-auto py-5 items-center px-[5vw] md:px-[80px]">

          <Link to={'/'} reloadDocument>  <img src="/morby_logo.svg" alt="Morby Logo" /> </Link>

          { (data.email || data.adminMode) && navigation()}

        </div>

        {/* Header border */}
        <div className="border-b border-[#666666]/40 w-full mb-[50px]"></div>

        <main className="w-full flex-1">
          <Outlet/>
        </main>

        <ScrollRestoration />
        <Toaster/>
        <Scripts />


      </body>


    </html>
  );
  
  
}


export default withSentry(Root)